import { useApp } from '../context/AppContext'
import type { Tab } from '../types/app'
import { leaveSettingsOk } from '../screens/settings/SettingsGroup'
import { Icon } from '../ui/Icon'
import type { IconName } from '../ui/Icon'
import './TabBar.css'

const TABS: { id: Tab; label: string; icon: IconName }[] = [
  { id: 'today', label: 'Today', icon: 'today' },
  { id: 'calendar', label: 'Calendar', icon: 'calendar' },
  { id: 'clients', label: 'Clients', icon: 'clients' },
  { id: 'earnings', label: 'Earnings', icon: 'earnings' },
  { id: 'goals', label: 'Goals', icon: 'goals' },
]

// currentNavItem is the one item the main navigation marks as current: the
// tab, or Settings while the menu or a Settings page is open (so Settings
// never leaves another tab looking selected).
export function currentNavItem(state: { activeTab: Tab; menuOpen: boolean; settingsPanel: unknown }): Tab | 'settings' {
  return state.menuOpen || state.settingsPanel ? 'settings' : state.activeTab
}

export default function TabBar() {
  const { state, dispatch } = useApp()
  const current = currentNavItem(state)

  const go = (tab: Tab) => {
    if (state.settingsPanel && !leaveSettingsOk()) return
    if (state.settingsPanel) dispatch({ type: 'CLOSE_SETTINGS_PANEL' })
    if (state.menuOpen) dispatch({ type: 'CLOSE_MENU' })
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab })
  }

  return (
    <nav className="tab-bar" aria-label="Main">
      {TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => go(tab.id)}
          className={`tab-button ${current === tab.id ? 'active' : ''}`}
          aria-current={current === tab.id ? 'page' : undefined}
        >
          <Icon name={tab.icon} size={22} className="tab-button__icon" />
          <span>{tab.label}</span>
        </button>
      ))}
      {/* Settings sits in the side rail from 1024px; on a phone it is the
          header button instead (hidden here by TabBar.css). */}
      <button
        type="button"
        className={`tab-button tab-button--settings ${current === 'settings' ? 'active' : ''}`}
        aria-current={current === 'settings' ? 'page' : undefined}
        onClick={() => {
          if (state.settingsPanel && !leaveSettingsOk()) return
          if (state.settingsPanel) dispatch({ type: 'CLOSE_SETTINGS_PANEL' })
          if (!state.menuOpen) dispatch({ type: 'TOGGLE_MENU' })
        }}
      >
        <Icon name="settings" size={22} className="tab-button__icon" />
        <span>Settings</span>
      </button>
    </nav>
  )
}
